import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Params } from '@angular/router';
import { IMaskModule } from 'angular-imask';
import { Subject, takeUntil } from 'rxjs';
import { DYNAMIC_FIELD_AUTO_POPULATE_HINT, DYNAMIC_FIELD_VALUE_MAPPING_HINT } from '../../utils/constants';
import { DynamicFieldOptionsFormComponent } from './components/dynamic-field-options-form/dynamic-field-options-form.component';
import { DynamicFieldValidationsFormComponent } from './components/dynamic-field-validations-form/dynamic-field-validations-form.component';
import {
    DynamicFieldForm,
    DynamicFieldMappingKeys,
    DynamicFieldPrefillKeys,
    DynamicFieldPrefillOption,
    DynamicFieldType,
    DynamicFieldValidation,
    DynamicFieldValidator,
} from './models/dynamic-field-form';

@Component({
    selector: 'app-dynamic-field-form',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatInputModule,
        MatCardModule,
        MatDividerModule,
        MatIconModule,
        MatButtonModule,
        MatSelectModule,
        MatSlideToggleModule,
        IMaskModule,
        DynamicFieldValidationsFormComponent,
        DynamicFieldOptionsFormComponent,
        CdkDragHandle,
    ],
    templateUrl: './dynamic-field-form.component.html',
    styleUrl: './dynamic-field-form.component.scss',
})
export class DynamicFieldFormComponent implements OnInit, OnDestroy {
    public fieldTypes: string[] = Object.values(DynamicFieldType).sort();
    private readonly unsubscribe$: Subject<void> = new Subject<void>();
    public fieldTypesWithOptions: DynamicFieldType[] = [
        DynamicFieldType.DROPDOWN,
        DynamicFieldType.AUTOCOMPLETE,
        DynamicFieldType.CHECKBOX,
        DynamicFieldType.RADIOBUTTON,
    ];
    public dynamicFieldValidators = Object.values(DynamicFieldValidator).sort();
    public nameMask = { mask: /^[A-Za-z0-9_]*$/ };
    public prefillKeys: DynamicFieldPrefillOption[] = DynamicFieldPrefillKeys;
    public mappingKeys: DynamicFieldPrefillOption[] = DynamicFieldMappingKeys;
    public readonly autoPopulateHint = DYNAMIC_FIELD_AUTO_POPULATE_HINT;
    public readonly valueMappingHint = DYNAMIC_FIELD_VALUE_MAPPING_HINT;

    @Input({ required: true }) id: number;
    @Input({ required: true }) form: FormGroup<DynamicFieldForm>;
    @Input({ required: true }) isPersistent: boolean;
    @Input() showDeleteButton: boolean = true;
    @Input() showDisabledToggle: boolean;
    @Input() showAutoPopulateAndMapping: boolean = false;
    @Input() dragEnabled: boolean = false;
    @Output() onDelete: EventEmitter<number> = new EventEmitter<number>();

    get fieldTypeCtrl(): FormControl<string | null> {
        return this.form.controls.fieldType as FormControl<string | null>;
    }

    get optionsCtrl(): FormArray<FormControl<string>> {
        return this.form.controls.options as FormArray<FormControl<string>>;
    }

    get selectedValidatorsCtrl(): FormControl<string[] | null> {
        return this.form.controls.selectedValidators as FormControl<string[]>;
    }

    get validationsCtrl(): FormArray<FormGroup<DynamicFieldValidation>> {
        return this.form.controls.validations as FormArray<FormGroup<DynamicFieldValidation>>;
    }

    get nameCtrl(): FormControl<string | null> {
        return this.form.controls.name as FormControl<string | null>;
    }

    get isDynamicCtrl(): FormControl<boolean | null> {
        return this.form.controls.isDynamic as FormControl<boolean | null>;
    }

    get prefillValueCtrl(): FormControl<string | null> {
        return this.form.controls.value as FormControl<string | null>;
    }

    get isValueMappingEnabledCtrl(): FormControl<boolean | null> | null {
        return this.form?.get('isValueMappingEnabled') as FormControl<boolean | null> | null;
    }

    get mappedFieldNameCtrl(): FormControl<string | null> | null {
        return this.form?.get('mappedFieldName') as FormControl<string | null> | null;
    }

    get showOptions(): boolean {
        return this.fieldTypesWithOptions.includes(this.fieldTypeCtrl.value as DynamicFieldType);
    }

    get hasValidationWithSettings(): boolean {
        return this.validationsCtrl.controls.some((ctrl) => {
            const validationName = ctrl.get('name')?.value;
            if (!validationName) return false;
            const excluded = ['required', 'email', 'awbValidator', 'invalid', 'multiple', 'preventedHTMLStr'];
            return !excluded.includes(validationName);
        });
    }

    ngOnInit(): void {
        this.nameCtrl.setValidators([Validators.required, Validators.pattern(/^[A-Za-z0-9_]+$/)]);
        this.nameCtrl.updateValueAndValidity();
        this.subscribeToFieldTypeChanges();
        this.subscribeSelectedValidators();
        this.reviewAutoPopulateAndMapping();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    delete(): void {
        this.onDelete.emit(this.id);
    }

    getUniqueId(suffix: string): string {
        const base = this.id !== null && this.id !== undefined ? this.id : Date.now();
        return `${suffix}-${base}`;
    }

    getNameValue(): string {
        return this.nameCtrl.value ?? '';
    }

    subscribeToFieldTypeChanges(): void {
        this.fieldTypeCtrl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
            if (!this.fieldTypesWithOptions.includes(value as DynamicFieldType)) {
                this.optionsCtrl.clear();
            }
        });
    }

    subscribeAutoPopulateChanges(): void {
        this.isDynamicCtrl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((isDynamic) => {
            if (!isDynamic) {
                this.prefillValueCtrl.setValue(null);
            }
        });
    }
    reviewAutoPopulateAndMapping(): void {
        if (this.showAutoPopulateAndMapping) {
            this.subscribeAutoPopulateChanges();
            this.subscribeMappingValueChanges();
        }
    }

    subscribeMappingValueChanges(): void {
        if (!this.isValueMappingEnabledCtrl || !this.mappedFieldNameCtrl) return;
        this.isValueMappingEnabledCtrl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((isEnabled) => {
            if (!isEnabled) {
                this.mappedFieldNameCtrl?.setValue(null);
            }
        });
    }

    subscribeSelectedValidators(): void {
        this.selectedValidatorsCtrl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((selected) => {
            const selectedArr = selected ?? [];
            const validationsArray = this.validationsCtrl;
            validationsArray.controls.forEach((ctrl, i) => {
                const validationName = ctrl.get('name')?.value;
                if (!selectedArr.includes(validationName ?? '')) {
                    validationsArray.removeAt(i);
                }
            });
            selectedArr.forEach((validator) => {
                this.addValidation(validator);
            });
        });
    }

    addValidation(validator: string): void {
        const validationsArray = this.validationsCtrl;
        const exists = validationsArray.controls.some((ctrl) => ctrl.get('name')?.value === validator);
        if (!exists) {
            let valueValidators = [Validators.required];
            if (validator === 'pattern') {
                valueValidators.push(this.patternValidator());
            } else {
                valueValidators.push(Validators.pattern(/^[\d]+$/));
            }
            const showSettings = this.showValidationSettings(validator);
            validationsArray.push(
                new FormGroup<DynamicFieldValidation>({
                    id: new FormControl<number | null>(null),
                    name: new FormControl<string | null>(validator),
                    value: showSettings
                        ? new FormControl<string | RegExp | string[] | RegExp[] | null>(null, valueValidators)
                        : new FormControl<string | RegExp | string[] | RegExp[] | null>(null),
                    message: new FormControl<string | null>(validator),
                    title: new FormControl<string | null>(null),
                    type: new FormControl<string | null>(null),
                    apiValidator: new FormControl<string | null>(null),
                    params: new FormControl<Params[] | null>(null),
                    typeRequest: new FormControl<string | null>(null),
                })
            );
        }
    }

    getValidationValueControl(
        validationCtrl: AbstractControl
    ): FormControl<string | RegExp | string[] | RegExp[] | null> | null {
        return validationCtrl.get('value') as FormControl<string | RegExp | string[] | RegExp[] | null> | null;
    }

    showValidationSettings(validationName: string | null | undefined): boolean {
        if (!validationName) return false;
        const excluded = ['required', 'email', 'awbValidator', 'invalid', 'multiple', 'preventedHTMLStr'];
        return !excluded.includes(validationName);
    }

    patternValidator() {
        return (control: AbstractControl) => {
            if (!control.value) return { required: true };
            let value = control.value;
            try {
                if (typeof value === 'string') {
                    value = value.trim();
                    if (value.startsWith('[') && value.endsWith(']')) {
                        const regexArray = value
                            .slice(1, -1)
                            .split(/,(?![^\"]*\"[^\"]*(?:\"[^\"]*\"[^\"]*)*$)/)
                            .map((s: String) => s.trim())
                            .filter(Boolean);
                        if (!regexArray.length) throw new Error('Empty array');
                        for (const regexStr of regexArray) {
                            this.validateSingleRegexString(regexStr);
                        }
                    } else {
                        throw new Error('Not an array');
                    }
                } else if (Array.isArray(value)) {
                    for (const regexStr of value) {
                        this.validateSingleRegexString(regexStr);
                    }
                } else {
                    throw new Error('Invalid type');
                }
                return null;
            } catch {
                return { patternInvalid: true };
            }
        };
    }

    private validateSingleRegexString(regexStr: string) {
        let pattern = regexStr.trim();
        if (pattern.startsWith('/') && pattern.lastIndexOf('/') > 0) {
            const lastSlash = pattern.lastIndexOf('/');
            const regexBody = pattern.substring(1, lastSlash);
            const regexFlags = pattern.substring(lastSlash + 1);
            new RegExp(regexBody, regexFlags);
        } else {
            throw new Error('Invalid regex');
        }
    }

    getValidationError(validationCtrl: AbstractControl): string | null {
        const valueCtrl = this.getValidationValueControl(validationCtrl);
        if (!valueCtrl || !valueCtrl.errors) return null;
        if (valueCtrl.errors['required']) return 'Value is required';
        if (valueCtrl.errors['patternInvalid']) return 'Invalid pattern';
        if (valueCtrl.errors['pattern']) return 'Only numbers are allowed';
        return null;
    }

    getValidationValueType(validationCtrl: AbstractControl): string {
        return validationCtrl.get('name')?.value === 'pattern' ? 'text' : 'number';
    }

    getValidationIMask(validationCtrl: AbstractControl): { mask: RegExp } | null {
        const name = validationCtrl.get('name')?.value;
        if (name === 'pattern') {
            return { mask: /^[^\r\n]*$/ };
        }
        return null;
    }

    showPatternFormatError(validationCtrl: AbstractControl): boolean {
        const name = validationCtrl.get('name')?.value;
        if (name !== 'pattern') return false;
        const valueCtrl = this.getValidationValueControl(validationCtrl);
        return !!(valueCtrl && valueCtrl.touched && valueCtrl.errors && valueCtrl.errors['patternInvalid']);
    }
}
